import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Spinner, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FETCH_FORMS_LIST } from 'actions/formActions';
import { FaEye, FaSearch, FaTimes } from 'react-icons/fa';

const FormList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading, allFormsList } = useSelector((state) => state.checklist);
    const [search, setSearch] = useState('');
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);

    useEffect(() => {
        dispatch({ type: FETCH_FORMS_LIST });
    }, [dispatch]);

    // Group forms by stationId and filter out invalid or empty stations
    const groupedByStation = useMemo(() => {
        console.log('Grouping forms by stationId:', allFormsList);
        return allFormsList.reduce((acc, form) => {
            if (!form.stationId || typeof form.stationId !== 'string') {
                console.warn('Skipping form with invalid stationId:', form);
                return acc;
            }
            if (!acc[form.stationId]) {
                acc[form.stationId] = [];
            }
            acc[form.stationId].push(form);
            return acc;
        }, {});
    }, [allFormsList]);

    // Build filtered and structured station data
    const filteredList = useMemo(() => {
        const result = Object.entries(groupedByStation)
            .filter(([stationId, forms]) => {
                const isValid = forms.length > 0;
                if (!isValid) {
                    console.log(`Excluding stationId ${stationId} with no forms`);
                }
                return isValid;
            })
            .filter(([stationId]) => stationId.toLowerCase().includes(search.toLowerCase()))
            .map(([stationId, forms]) => {
                if (!forms[0]) {
                    console.warn(`No valid forms for stationId ${stationId}`);
                    return null;
                }
                const totalForms = forms.length;
                const approvedForms = forms.filter((f) => f.status === 'approved').length;
                const draftForms = forms.filter((f) => f.status === 'draft').length;
                const pendingStatuses = ['QUALITY', 'MANAGER', 'CONTRACTOR', 'PENDING_MANAGER'];
                const pendingForms = forms.filter(f => pendingStatuses.includes(f.status) || (f.status || '').toLowerCase().includes('pending')).length;
                const completionPercentage = totalForms > 0 ? Math.round((approvedForms / totalForms) * 100) : 0;
                const { project_name, userName } = forms[0];
                console.log(
                    `Station ${stationId}: ${approvedForms} approved, ${draftForms} draft, ${pendingForms} pending, ${totalForms} total, ${completionPercentage}% complete`
                );
                return {
                    stationId,
                    project_name: project_name || 'N/A',
                    userName: userName || 'N/A',
                    status: approvedForms === totalForms && totalForms > 0 ? 'completed' : 'in-progress',
                    formCount: totalForms,
                    completionPercentage,
                    approvedCount: approvedForms,
                    draftCount: draftForms,
                    pendingCount: pendingForms,
                };
            })
            .filter((item) => item !== null)
            .sort((a, b) => a.stationId.localeCompare(b.stationId)); // Sort by stationId for consistency

        console.log('Filtered station list:', result);
        return result;
    }, [groupedByStation, search]);


    return (
        <div className="page-body checklist-container">
            <div className="container-xl">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="page-title m-0">Checklist Overview</h2>
                    <Badge bg="light" className="text-dark border px-3 py-2" style={{ borderRadius: '10px' }}>
                        Total Stations: {filteredList.length}
                    </Badge>
                </div>

                <div className="premium-filter-section mb-4">
                    <div className={`filter-group search-wrapper ${search || isSearchExpanded ? 'expanded' : ''}`}>
                        <label htmlFor="station-search">Search Stations</label>
                        <div className="position-relative">
                            <Form.Control
                                id="station-search"
                                type="text"
                                placeholder="Search by Station ID..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onFocus={() => setIsSearchExpanded(true)}
                                onBlur={() => !search && setIsSearchExpanded(false)}
                            />
                            <FaSearch className="search-icon" />
                        </div>
                    </div>

                    {search && (
                        <div className="filter-group flex-grow-0">
                            <span className="filter-label-spacer" aria-hidden="true">&nbsp;</span>
                            <button 
                                id="clear-search-btn"
                                className="btn-clear" 
                                onClick={() => setSearch('')} 
                                title="Clear Search"
                            >
                                <FaTimes /> Clear
                            </button>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="text-center my-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th>Station ID</th>
                                    <th>Project Name</th>
                                    <th>User</th>
                                    <th>Total Forms</th>
                                    <th>Approved</th>
                                    <th>Draft</th>
                                    <th>Pending</th>
                                    <th>Completion</th>
                                    <th>Site Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredList.map((form) => (
                                    <tr key={form.stationId}>
                                        <td>
                                            <span 
                                                className="fw-bold" 
                                                role="button"
                                                tabIndex="0"
                                                style={{ color: '#1abc9c' }}
                                                onClick={() => navigate(`/formList/${form.stationId}`)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        navigate(`/formList/${form.stationId}`);
                                                    }
                                                }}
                                            >
                                                {form.stationId}
                                            </span>
                                        </td>
                                        <td><span className="text-muted small">{form.project_name}</span></td>
                                        <td><span className="text-muted small">{form.userName}</span></td>
                                        <td><Badge bg="light" className="text-dark">{form.formCount}</Badge></td>
                                        <td><span className="text-success fw-bold">{form.approvedCount}</span></td>
                                        <td><span className="text-primary">{form.draftCount}</span></td>
                                        <td><span className="text-warning">{form.pendingCount}</span></td>
                                        <td style={{ minWidth: '120px' }}>
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="premium-progress flex-grow-1">
                                                    <div className="bar" style={{ width: `${form.completionPercentage}%` }}></div>
                                                </div>
                                                <span className="small fw-bold">{form.completionPercentage}%</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`status-pill status-${form.status}`}>
                                                {form.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="action-btn-group">
                                                <button
                                                    className="btn-premium"
                                                    title="View Station Forms"
                                                    onClick={() => navigate(`/formList/${form.stationId}`)}
                                                >
                                                    <FaEye />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filteredList.length === 0 && (
                                    <tr>
                                        <td colSpan="10" className="text-center py-5 text-muted">
                                            No forms found matching your search.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FormList;